import React from 'react';
import { Info } from 'lucide-react';
import { cn } from '../../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '../ui/Tooltip';

interface FilterFieldShellProps {
  /** Optional override label. Most controls render their own internal label,
   *  in which case this prop is omitted. */
  label?: string | undefined;
  /** Long-form description shown only when the user hovers/focuses the
   *  info icon. Keeps each row visually compact. */
  description?: string | undefined;
  /** Optional secondary description (e.g. detailed copy under shortDescription). */
  secondary?: string | undefined;
  /** Optional decorator rendered next to the description icon (e.g. unit hint). */
  helper?: React.ReactNode | undefined;
  /** Disable the info icon entirely (useful when description is empty). */
  hideInfo?: boolean | undefined;
  className?: string | undefined;
  children: React.ReactNode;
}

/**
 * FilterFieldShell - wraps a single filter control with an optional label
 * and a tooltip-driven description, replacing the older always-visible
 * info-block layout. Intentionally lightweight: it owns no value state.
 */
export const FilterFieldShell: React.FC<FilterFieldShellProps> = ({
  label,
  description,
  secondary,
  helper,
  hideInfo = false,
  className,
  children,
}) => {
  const hasInfo = !hideInfo && (description || secondary);

  return (
    <div className={cn('flex flex-col gap-1 min-w-0', className)}>
      {(label || hasInfo) && (
        <div className="flex items-start gap-1.5 flex-wrap min-w-0">
          {label && (
            <span className="text-sm font-medium text-text-secondary whitespace-normal break-words">
              {label}
            </span>
          )}
          {hasInfo && (
            <TooltipProvider delayDuration={120}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`${label ?? 'field'} description`}
                    className={cn(
                      'flex items-center justify-center shrink-0',
                      // Generous hitbox on touch (44×44) while keeping the visible
                      // glyph compact - Apple HIG / WCAG 2.5.5.
                      'min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 -my-2 md:my-0 -mx-2 md:mx-0',
                      'rounded-full text-text-muted',
                      'hover:text-accent-primary focus:outline-none',
                      'focus-visible:ring-2 focus-visible:ring-accent-primary'
                    )}
                    tabIndex={0}
                  >
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="start"
                  className="max-w-[280px] text-xs leading-snug"
                >
                  {description && <div>{description}</div>}
                  {secondary && (
                    <div className="mt-1 text-xs opacity-80">
                      {secondary}
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {helper && (
            <span className="ml-auto text-xs text-text-muted">
              {helper}
            </span>
          )}
        </div>
      )}
      <div className="min-w-0">{children}</div>
    </div>
  );
};

export default FilterFieldShell;
